import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { Command } from '../pages/Commands';
import { Money } from './format';

// Lo de admin (P1-D10): what a LODE command or a lo de CONFIG command does, readable for the
// checker who approves it.

export type LodeCategory = { id: number; code: string; name: string; region: string; type: string; pay_number: number; rate: number; gia_ap_dung: number; rate_ap_dung: number; max_number: number; digit: number };
export type LodeInfo = {
  tyLeTran: number; tyLeLuc: string | null; hoanSauGio: number;
  mien: Record<string, { soMien: number; gioDong: string; gioChot: string }>;
  dai: { id: number; name: string; region: string; thu: number[] }[];
  danhMuc: LodeCategory[];
  cauTruc: Record<string, Record<string, [number, number]>>;
  keno: { chuKy: number };
  sieuToc: { kenh: { id: number; name: string; chuKy: number }[]; kenhCu: Record<string, number> };
};

export const REGION: Record<string, string> = { bac: 'Miền Bắc', trung: 'Miền Trung', nam: 'Miền Nam' };
export const PRIZES = ['special', 'prize1', 'prize2', 'prize3', 'prize4', 'prize5', 'prize6', 'prize7', 'prize8'] as const;
export const PRIZE_NAME: Record<string, string> = {
  special: 'Đặc biệt', prize1: 'Giải nhất', prize2: 'Giải nhì', prize3: 'Giải ba', prize4: 'Giải tư',
  prize5: 'Giải năm', prize6: 'Giải sáu', prize7: 'Giải bảy', prize8: 'Giải tám',
};
export const LODE_ACTION: Record<string, string> = { nhap_kq: 'Nhập kết quả', chot_lai: 'Chốt lại kết quả', huy: 'Hủy kỳ, hoàn vé', hoan_ve: 'Hoàn vé' };

export function useLodeInfo() {
  const { can } = useAuth();
  return useQuery({ queryKey: ['lode-info'], queryFn: () => api.get<LodeInfo>('/api/admin/lode/info'), enabled: can('lode.view'), staleTime: 60_000 });
}

export function lodeSummary(p: Record<string, unknown>) {
  const act = LODE_ACTION[String(p.hanh_dong)] ?? String(p.hanh_dong);
  if (p.hanh_dong === 'hoan_ve') return `Lô đề: ${act} #${String(p.ve_id)}`;
  return `Lô đề: ${act} đài ${String(p.city_id)} ngày ${String(p.ngay)}`;
}

export function Board({ board, shape }: { board: Record<string, string>; shape?: Record<string, [number, number]> }) {
  return (
    <dl className="facts lode-board">
      {PRIZES.filter((g) => board[g] || (shape?.[g]?.[0] ?? 0) > 0).map((g) => (
        <div key={g} style={{ display: 'contents' }}>
          <dt>{PRIZE_NAME[g]}</dt>
          <dd className={'num' + (g === 'special' ? ' lode-board__special' : '')}>{String(board[g] ?? '').split('-').filter(Boolean).join('  ') || '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LodeChange({ c }: { c: Command }) {
  const info = useLodeInfo().data;
  const p = c.payload;
  const st = info?.dai.find((d) => d.id === Number(p.city_id));
  return (
    <div className="stack">
      <dl className="facts">
        <dt>Việc</dt><dd>{LODE_ACTION[String(p.hanh_dong)] ?? String(p.hanh_dong)}</dd>
        {p.hanh_dong === 'hoan_ve' ? (
          <><dt>Vé</dt><dd className="num">#{String(p.ve_id)}</dd></>
        ) : (
          <>
            <dt>Đài</dt><dd>{st ? `${st.name} (${REGION[st.region] ?? st.region})` : String(p.city_id)}</dd>
            <dt>Ngày quay</dt><dd className="num">{String(p.ngay)}</dd>
          </>
        )}
        <dt>Lý do</dt><dd>{String(p.ly_do ?? '')}</dd>
      </dl>
      {p.hanh_dong === 'huy' ? <p>Mọi vé chưa hoàn của kỳ này được hoàn tiền cược; vé đã trả thưởng bị thu lại phần thưởng. Vé đã được chủ sở hữu sửa kết quả giữ nguyên.</p> : null}
      {p.hanh_dong === 'hoan_ve' ? <p>Vé chưa chốt: trả lại tiền cược. Vé đã chốt: trả lại tiền cược và thu lại tiền thưởng đã trả, ví chỉ đổi phần chênh.</p> : null}
      {p.hanh_dong === 'chot_lai' ? <p>Mọi vé đã chốt được tính lại theo bảng mới; ví chỉ đổi phần chênh, không đủ tiền thu hồi thì ghi nợ.</p> : null}
      {p.hanh_dong === 'nhap_kq' ? <p>Sau khi hệ A ghi bảng này, các vé đang chờ của đài được chốt một lần theo luật thường.</p> : null}
      {p.bang ? <Board board={p.bang as Record<string, string>} shape={st ? info?.cauTruc[st.region] : undefined} /> : null}
    </div>
  );
}

type Obj = Record<string, any>;

export function LodeRateChange({ doc }: { doc: Obj }) {
  const info = useLodeInfo().data;
  const rates = (doc.tyLe ?? {}) as Record<string, number>;
  const prices = (doc.gia ?? {}) as Record<string, number>;
  const ids = [...new Set([...Object.keys(prices), ...Object.keys(rates)])];
  if (!ids.length) return <p className="muted">Mọi loại cược về giá và tỷ lệ gốc của game.</p>;
  return (
    <table className="plain">
      <thead><tr><th>Loại cược</th><th className="r">Giá gốc</th><th className="r">Giá sau lệnh</th><th className="r">Tỷ lệ gốc</th><th className="r">Tỷ lệ sau lệnh</th></tr></thead>
      <tbody>
        {ids.map((id) => {
          const cat = info?.danhMuc.find((x) => x.id === Number(id));
          return (
            <tr key={id}>
              <td className="is-title">{cat ? `${cat.name} (${REGION[cat.region] ?? cat.region})` : id}</td>
              <td className="r num" data-label="Giá gốc">{cat?.pay_number ?? '-'}</td>
              <td className="r num" data-label="Giá sau lệnh">{prices[id] ?? cat?.pay_number ?? '-'}</td>
              <td className="r num" data-label="Tỷ lệ gốc">{cat?.rate ?? '-'}</td>
              <td className="r num" data-label="Tỷ lệ sau lệnh">{rates[id] ?? cat?.rate ?? '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function LodeLimitChange({ doc }: { doc: Obj }) {
  const info = useLodeInfo().data;
  const station = (id: number) => (id === 0 ? 'Mọi đài' : info?.dai.find((d) => d.id === id)?.name ?? String(id));
  const type = (code: string) => (code === '' ? 'Mọi loại' : info?.danhMuc.find((c) => c.code === code)?.name ?? code);
  const chan = (doc.chanSo ?? []) as { dai: number; ma: string; so: string; lyDo?: string }[];
  const tran = (doc.tranSo ?? []) as { dai: number; ma: string; toiDa: number }[];
  return (
    <div className="stack">
      <h3 className="lode-h3">Số chặn ({chan.length})</h3>
      {chan.length ? (
        <table className="plain"><tbody>{chan.map((e, i) => (
          <tr key={i}><td className="num is-title">{e.so}</td><td>{station(e.dai)}</td><td>{type(e.ma)}</td><td className="muted">{e.lyDo ?? ''}</td></tr>
        ))}</tbody></table>
      ) : <p className="muted">Không chặn số nào.</p>}
      <h3 className="lode-h3">Trần tiền một số ({tran.length})</h3>
      {tran.length ? (
        <table className="plain"><tbody>{tran.map((e, i) => (
          <tr key={i}><td>{station(e.dai)}</td><td>{type(e.ma)}</td><td className="r"><Money v={e.toiDa} unit /></td></tr>
        ))}</tbody></table>
      ) : <p className="muted">Không có trần riêng.</p>}
    </div>
  );
}
